import { TableClient } from '@azure/data-tables'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CUSTOM_JSON_THEME, DATA_SERVICES_TABLES, DATA_SERVICES_TABLES_DEV } from '../common/common.constants'
import { InteractiveBrowserCredential } from '@azure/identity'
import { TENANT_ID, CLIENT_ID } from '../authConfig'
import { useLoading } from '../loading/LoadingContext'
import { Accordion, Badge, Button, ButtonGroup, Form, ToggleButton } from 'react-bootstrap'
import BlobModalComponent from '../blobs/BlobModalComponent'
import EyeSvg from '../common/components/svg/EyeSvg'
import JSONPretty from 'react-json-pretty'
import { apiFetch } from '../utility/fetch'
import { useUserContext } from '../user/UserContext'
import { notify } from '../notify/notify'
import { DateTime } from 'luxon'
import CopySvg from '../common/components/svg/CopySvg'

export default function Inventory(){
  const {toggleLoading} = useLoading()
  const {userData} = useUserContext()
  const [environment, setEnvironment] = useState('production')
  const [blobData, setBlobData] = useState(undefined)
  const [blobDataRaw, setBlobDataRaw] = useState(undefined)
  const [blobName, setBlobName] = useState(undefined)
  const [blobVisible, setBlobVisible] = useState(false)
  const [jobs, setJobs] = useState(undefined)
  const [configs, setConfigs] = useState(undefined)
  const [selectedMarketplace, setSelectedMarketplace] = useState(undefined)
  const [searchSku, setSearchSku] = useState(undefined)
  const [marketplaceFuncs, setMarketplaceFuncs] = useState(undefined)
  const [results, setResults] = useState([])
  const [searchType, setSearchType]: ['SKU' | 'Marketplace', any] = useState('SKU')
  
  const credential = new InteractiveBrowserCredential({
    tenantId: TENANT_ID,
    clientId: CLIENT_ID,
    authorityHost: 'https://login.microsoft.com'
  })

  useEffect(() => {
    const loadConfigs = async () => {
      toggleLoading(true)
      const configResponse = await apiFetch<any[]>({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        params: [{key: 'table', value: 'integrationConfiguration'}]
      })
      if(!configResponse.ok){
        notify('error', JSON.stringify(configResponse.body))
        toggleLoading(false)
        return
      }

      setConfigs(configResponse.body.filter(r => typeof r.inventoryPartitionKey !== 'undefined'))
      const tempConfigs: any[] = configResponse.body as any[]

      const jobsResponse = await apiFetch({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        params: [{key: 'table', value: 'inventoryProcessingJobs'}]
      })
      setJobs(jobsResponse.body)

      const marketplaceFuncMap = new Map()
      for(const config of tempConfigs){
        if(!marketplaceFuncMap.has(config.partitionKey)){
          marketplaceFuncMap.set(config.partitionKey, [])
        }
        if(config.inventoryPartitionKey){
          marketplaceFuncMap.get(config.partitionKey).push(config.inventoryPartitionKey)
        }
        if(config.metafieldsPartitionKey){
          marketplaceFuncMap.get(config.partitionKey).push(config.metafieldsPartitionKey)
  
        }
        if(config.productPartitionKey){
          marketplaceFuncMap.get(config.partitionKey).push(config.productPartitionKey)
  
        }
        if(config.productsPartitionKey){
          marketplaceFuncMap.get(config.partitionKey).push(config.productsPartitionKey)
  
        }
        if(config.variantsPartitionKey){
          marketplaceFuncMap.get(config.partitionKey).push(config.variantsPartitionKey)
  
        }
        if(config.logisticsPartitionKey){
          marketplaceFuncMap.get(config.partitionKey).push(config.logisticsPartitionKey)
  
        }
        if(config.pricingPartitionKey){
          marketplaceFuncMap.get(config.partitionKey).push(config.pricingPartitionKey)
  
        }
      }
      setMarketplaceFuncs(marketplaceFuncMap)
      toggleLoading(false)
    }
    loadConfigs()
  }, [environment])

  const handleViewConfig = useCallback((e) => {
    const {pkey, rkey} = e.currentTarget.dataset
    const config = configs.find(c => c.partitionKey === pkey && c.rowKey === rkey)
    const data = {
      integrationConfiguration: config,
      inventoryProcessingJobs: jobs?.filter(j => marketplaceFuncs.get(config.partitionKey).some(f => f === j.partitionKey))
    }
    setBlobName(`${pkey} ${rkey}`)
    setBlobData(<JSONPretty data={data} theme={CUSTOM_JSON_THEME} style={{fontSize: '1.5em'}} />)
    setBlobDataRaw(JSON.stringify(data))
    setBlobVisible(true)
  }, [configs, marketplaceFuncs, jobs])

  const handleSearchBySkuClicked = useCallback((e) => {
    const load = async function() {
      if(!searchSku || searchSku.trim().length === 0) return
      toggleLoading(true)
      const response = await apiFetch({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        params: [{key: 'table', value: 'inventoryStatus'}, {key: 'filter', value: `sku eq '${searchSku}'`}]
      })
      setResults(response.body as any[])
      toggleLoading(false)
    }
    load()
  }, [searchSku])

  const handleSearchByMarketplaceClicked = useCallback((e) => {
    const load = async function() {
      if(!selectedMarketplace) return
      toggleLoading(true)
      const response = await apiFetch({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        params: [{key: 'table', value: 'inventoryStatus'}, {key: 'filter', value: `PartitionKey eq '${selectedMarketplace}'`}]
      })
      setResults(response.body as any[])
      toggleLoading(false)
    }
    load()
  }, [selectedMarketplace])

  const handleCopyClicked = useCallback((e) => {
    const copy = async function(){
      await navigator.clipboard.writeText(e)
      notify('success', 'Copied content to clipboard!')
    }
    copy()
  }, [])

  const view = useMemo(() => {
    if(!configs) return <></>

    const options: {value: string, display: string}[] = []
    for(const config of configs){
      if(config.inventoryFunctionName){
        options.push({value: config.inventoryPartitionKey, display: config.inventoryLogMessageTitle ?? config.inventoryPartitionKey})
      }
      if(config.metafieldsFunctionName){
        options.push({value: config.metafieldsFunctionName, display: config.metaFieldsLogMessageTitle ?? config.metafieldsFunctionName})
      }
      if(config.productFunctionName){
        options.push({value: config.productFunctionName, display: config.productLogMessageTitle ?? config.productsLogMessageTitle ?? config.productFunctionName})
      }
      if(config.productsFunctionName){
        options.push({value: config.productsFunctionName, display: config.productsLogMessageTitle ?? config.productLogMessageTitle ?? config.productsFunctionName})
      }
      if(config.variantsFunctionName){
        options.push({value: config.variantsFunctionName, display: config.variantsLogMessageTitle ?? config.variantsFunctionName})
      }
      if(config.logisticsFunctionName){
        options.push({value: config.logisticsFunctionName, display: config.logisticsLogMessageTitle ?? config.logisticsFunctionName})
      }
      if(config.pricingFunctionName){
        options.push({value: config.pricingFunctionName, display: config.pricingLogMessageTitle ?? config.pricingFunctionName})
      }
    }
    return <div>
      <BlobModalComponent 
        blobData={blobData}
        blobName={blobName}
        closeModal={() => {setBlobVisible(false)}}
        modalVisible={blobVisible}
        raw={blobDataRaw}
      />
      <div className='beacon-card'>
        <table className='beacon-table'>
          <tbody>
            <tr>
              <td><span className='beacon-header'>INVENTORY</span></td>
            </tr>
            <tr>
              <td>
                <span className='beacon-label'>View configuration settings or search by SKU, or Marketplace, to see all of the related inventory mapping status records that were last sent to the marketplaces.</span>
              </td>
            </tr>
            <tr>
              <td>
                <div>
                  <span className='beacon-label'>environment</span>
                  <br/>
                  <ButtonGroup>
                    <ToggleButton
                      type="radio"
                      variant={'secondary'}
                      value="development"
                      checked={environment === 'development'}
                      onChange={() => setEnvironment('development')}
                      id="developmentRadio"
                      className={`px-4 ${environment === 'development' ? 'active' : ''}`}
                    >
                      TEST
                    </ToggleButton>
                    <ToggleButton
                      type="radio"
                      variant={'secondary'}
                      value="production"
                      checked={environment === 'production'}
                      onChange={() => setEnvironment('production')}
                      id="productionRadio"
                      className={`px-4 ${environment === 'production' ? 'active' : ''}`}
                    >
                      PROD
                    </ToggleButton>
                  </ButtonGroup>
                </div> 
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <Accordion className='custom-accordion'>
        <Accordion.Item eventKey='0'>
          <Accordion.Header className='sticky-row'><span className=''>CONFIGURATIONS</span></Accordion.Header>
          <Accordion.Body>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
              {configs?.map(c => (
                <div className='beacon-card' style={{width: 'auto', height: 'auto', maxWidth: '100%'}}>
                  <span style={{float: 'right', cursor: 'pointer'}} title={'View the Azure content in the browser'} data-pkey={c.partitionKey} data-rkey={c.rowKey} onClick={handleViewConfig}>
                    <EyeSvg height={16}/>
                  </span>
                  <span className='beacon-header' style={{fontSize: 'medium'}}>{c.partitionKey}<br/>{c.rowKey}</span>
                  <hr/>
                  <span className='beacon-label'>
                    <Badge bg={c.inventoryEnabled === true ? 'success' : 'danger'}>
                      {c.inventoryEnabled === true ? 'Inventory Enabled' : 'Inventory Disabled'}
                    </Badge>
                  </span>&nbsp;
                  <span className='beacon-label'>
                    <Badge bg={c.inventoryFullFeed === true ? 'success' : 'warning'} style={{color: c.inventoryFullFeed === true ? 'white' : 'black'}}>
                      {c.inventoryFullFeed === true ? 'Full Feed' : 'Change Feed'}
                    </Badge>
                  </span>
                  <Accordion>
                    <Accordion.Item eventKey='0'>
                      <Accordion.Header>WAREHOUSES</Accordion.Header>
                      <Accordion.Body>
                        {c.inventoryWarehouses ? (
                          JSON.parse(c.inventoryWarehouses).map(wh => (
                            <span style={{display: 'inline-flex'}}>
                            <Badge bg='secondary'>
                              {wh}
                            </Badge>
                              &nbsp;
                            </span>
                          ))
                        ): ''}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                  <Accordion>
                    <Accordion.Item eventKey='0'>
                        <Accordion.Header>JOBS</Accordion.Header>
                        <Accordion.Body>
                          {marketplaceFuncs?.get(c.partitionKey)?.map(func => (
                            <div className='beacon-label'>
                              {func} - {jobs?.find(j => j.partitionKey === func)?.status} - {jobs?.find(j => j.partitionKey === func)?.timestamp}
                            </div>
                          ))}
                        </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>

                  <br/>
                  
                </div>
              ))}
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <Accordion defaultActiveKey={'0'} className='custom-accordion'>
        <Accordion.Item eventKey='0'>
          <Accordion.Header><span className=''>FILTERS</span></Accordion.Header>
          <Accordion.Body>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
              <div className='beacon-card' style={{width: '200px'}}>
                <span className='beacon-header'>Search by SKU</span>
                <Form.Control placeholder='Enter SKU...' onChange={(e) => {setSearchType('SKU'); setSearchSku(e.currentTarget.value)}}></Form.Control>
                <Button onClick={handleSearchBySkuClicked} variant='secondary'>Search</Button>
              </div>
              <div className='beacon-card' style={{width: '250px'}}>
                <span className='beacon-header'>Select Marketplace</span>
                <Form.Select className="fs-5" onChange={(e) => {setSearchType('Marketplace'); setSelectedMarketplace(e.currentTarget.value)}}>
                  <option selected disabled>Select marketplace</option>
                  {options.map(o => (
                    <option value={o.value}>{o.display}</option>
                  ))}
                </Form.Select>
                <Button onClick={handleSearchByMarketplaceClicked} variant='secondary'>Search</Button>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <span className='beacon-header'>Results</span><br/>
      {results?.map(r => (
        <>
        <div className='beacon-card'>
          <span className='beacon-header' style={{float: 'right'}}>
            {configs?.find(c => c.rowKey === decodeURIComponent(r.rowKey).split('|')[0])?.partitionKey} {decodeURIComponent(r.rowKey)}
          </span>
          
          <span className='beacon-header'>{r.sku}</span>&nbsp;<span className='beacon-label'>{DateTime.fromISO(r.timestamp).toFormat('MM/dd/yyyy t')}</span>
          &nbsp;&nbsp;
          <span title='Copy content to clipboard' style={{cursor: 'pointer'}} onClick={(e) => {handleCopyClicked(r.payload)}}>
            <CopySvg height={20}/>
          </span>
          <Form.Control as='textarea' rows={3} type='text' size='lg' value={r.payload}></Form.Control>
        </div>
        <br/>
        </>
        
      ))}
    </div>
  }, [
    configs, 
    results, 
    searchType, 
    selectedMarketplace, 
    searchSku,
    blobData,
    blobDataRaw,
    blobName,
    blobVisible,
    jobs,
    marketplaceFuncs
  ])

  return view
}
